/**
 * L'avertissement « cette fiche existe peut-être déjà ».
 *
 * Dit CE QUI existe, POURQUOI on le rapproche et OÙ c'est, avec un lien vers la fiche (nouvel onglet : le
 * formulaire en cours n'est pas perdu). Le bouton « créer quand même » n'apparaît que si une fiche
 * ressemble vraiment (même objet) : un simple « même numéro, autre marque » n'arrête rien.
 *
 * Même composant pour les machines, les clients et les sites : seul le vocabulaire change.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  libelleConfirmation,
  lienFiche,
  phraseNumero,
  separer,
  titreAvertissement,
  type Doublon,
  type ObjetDoublon,
} from '../domaine/doublons';

export function AvertissementDoublons({
  objet,
  doublons,
  correction = false,
  enCours = false,
  defiler = false,
  onConfirmer,
}: {
  objet: ObjetDoublon;
  doublons: readonly Doublon[];
  /** Vrai dans le formulaire de correction : le bouton dit « enregistrer quand même ». */
  correction?: boolean;
  enCours?: boolean;
  /** Ramène l'avertissement à l'écran (quand le serveur vient de refuser l'enregistrement). */
  defiler?: boolean;
  /** Confirmer = « ce n'est pas la même fiche ». Absent : l'avertissement est seulement informatif. */
  onConfirmer?: (() => void) | undefined;
}) {
  const racine = useRef<HTMLDivElement>(null);
  const { memeObjet, memeNumero } = separer(doublons);

  useEffect(() => {
    // Saut direct, sans animation : une animation n'avance pas dans un onglet en arrière-plan.
    if (defiler) racine.current?.scrollIntoView({ block: 'nearest' });
  }, [defiler, doublons]);

  if (doublons.length === 0) return null;

  return (
    <div ref={racine} aria-live="polite">
      {memeObjet.length > 0 && (
        <div className="message avertissement avertissement-doublon" role="alert">
          <strong>{titreAvertissement(objet, memeObjet)}</strong>
          <ul className="liste-doublons">
            {memeObjet.map((d) => (
              <li key={d.id}>
                <span>
                  <span className="doublon-titre">{d.titre}</span> · {d.etat}
                </span>
                <span className="doublon-detail">
                  {d.raison}
                  {d.ou ? ` · ${d.ou}` : ''}
                </span>
                <Link to={lienFiche(objet, d.id)} target="_blank" rel="noopener noreferrer">
                  Ouvrir la fiche (nouvel onglet)
                </Link>
              </li>
            ))}
          </ul>
          {onConfirmer && (
            <button type="button" className="secondaire" disabled={enCours} onClick={onConfirmer}>
              {libelleConfirmation(objet, correction, memeObjet)}
            </button>
          )}
        </div>
      )}

      {memeNumero.length > 0 && (
        <div className="message info">
          {phraseNumero(memeNumero)}
          {memeNumero.length === 1 && memeNumero[0] && (
            <>
              {' '}
              <Link to={lienFiche(objet, memeNumero[0].id)} target="_blank" rel="noopener noreferrer">
                Voir sa fiche
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
